import { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ITEM_STATUSES,
  meetingsApi,
  type ItemKind,
  type ItemStatus,
  type MeetingItem,
} from '@/api/meetings';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { KindChip } from '@/components/KindChip';
import { ErrorState } from '@/components/ErrorState';
import { StatusChip } from '@/components/StatusChip';
import { statusLabel } from '@/components/statusLabel';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { ApiError } from '@/api/errors';
import { activeCount, defaultsOf, enumParam, useUrlState } from '@/shared/useUrlState';

const KINDS = Object.keys(ITEM_STATUSES) as ItemKind[];
const STATUSES = [...new Set(Object.values(ITEM_STATUSES).flat())];
// `kind` and `status` are in the URL; `all` kinds and `open` items are the defaults, so omitted.
const URL_SCHEMA = {
  kind: enumParam<ItemKind | 'all'>(['all', ...KINDS], 'all'),
  status: enumParam<ItemStatus | 'open' | 'all'>(['open', 'all', ...STATUSES], 'open'),
};
const FILTERS = ['kind', 'status'] as const;
const CLOSED = new Set<ItemStatus>(['DONE', 'DROPPED', 'RETIRED', 'MET', 'ANSWERED']);

export function VirtualAccountItemsTab({ virtualAccountId }: { virtualAccountId: number }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const [meetings, setMeetings] = useState<{
    key: string;
    names: Map<number, string>;
  } | null>(null);
  const [filters, setFilters] = useUrlState(URL_SCHEMA);
  const { status } = filters;
  const kind = filters.kind === 'all' ? undefined : filters.kind;
  const [tick, setTick] = useState(0);
  const key = `${virtualAccountId}|${kind ?? 'all'}|${status}|${tick}`;
  const [state, setState] = useState<{ key: string; items: MeetingItem[] } | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ key: string; error: unknown } | null>(null);
  const items = state?.key === key ? state.items : null;
  const instants = (items ?? []).flatMap((item) => {
    const instant = Date.parse(item.occurrenceKey);
    return Number.isFinite(instant) ? [instant] : [];
  });
  const day = 24 * 60 * 60 * 1000;
  const from = instants.length
    ? new Date(Math.min(...instants) - day).toISOString().slice(0, 10)
    : null;
  const to = instants.length
    ? new Date(Math.max(...instants) + day).toISOString().slice(0, 10)
    : null;
  const meetingsKey = `${virtualAccountId}|${from}|${to}`;
  useEffect(() => {
    if (from === null || to === null) return;
    let live = true;
    void meetingsApi
      .listForVirtualAccount(virtualAccountId, { from, to })
      .catch((error: unknown) => {
        if (live && error instanceof ApiError && error.status === 400) {
          return meetingsApi.listForVirtualAccount(virtualAccountId);
        }
        throw error;
      })
      .then((calendar) => {
        if (live) {
          setMeetings({
            key: meetingsKey,
            names: new Map(calendar.items.map((entry) => [entry.meetingId, entry.title])),
          });
        }
      })
      .catch(() => {
        if (live) setMeetings({ key: meetingsKey, names: new Map() });
      });
    return () => {
      live = false;
    };
  }, [virtualAccountId, from, to, meetingsKey]);
  useEffect(() => {
    let live = true;
    const query: { kind?: ItemKind; status?: ItemStatus } = {};
    if (kind) query.kind = kind;
    if (status !== 'all' && status !== 'open') query.status = status;
    void meetingsApi
      .listVirtualAccountItems(virtualAccountId, query)
      .then((items) => {
        if (live) {
          setState({
            key,
            items:
              status === 'open'
                ? items.filter((item) => item.status !== null && !CLOSED.has(item.status))
                : items,
          });
          setLoadFailure((previous) => (previous?.key === key ? null : previous));
        }
      })
      .catch((error: unknown) => {
        if (live) setLoadFailure({ key, error });
      });
    return () => {
      live = false;
    };
  }, [virtualAccountId, kind, status, key]);
  const failed = loadFailure?.key === key ? loadFailure : null;
  const names = meetings?.key === meetingsKey ? meetings.names : null;
  return (
    <Box>
      <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="virtual-account-items-kind">{t('virtualAccountItems.kind')}</InputLabel>
          <Select
            labelId="virtual-account-items-kind"
            label={t('virtualAccountItems.kind')}
            value={kind ?? 'all'}
            onChange={(event) => setFilters({ kind: event.target.value as ItemKind | 'all' })}
          >
            <MenuItem value="all">{t('virtualAccountItems.allKinds')}</MenuItem>
            {KINDS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`virtualAccountItems.kinds.${value}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="virtual-account-items-status">
            {t('virtualAccountItems.status')}
          </InputLabel>
          <Select
            labelId="virtual-account-items-status"
            label={t('virtualAccountItems.status')}
            value={status}
            onChange={(event) =>
              setFilters({ status: event.target.value as ItemStatus | 'open' | 'all' })
            }
          >
            <MenuItem value="open">{t('virtualAccountItems.open')}</MenuItem>
            <MenuItem value="all">{t('virtualAccountItems.allStatuses')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {statusLabel(t, 'meetingItem', value)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ClearFiltersButton
          active={activeCount(URL_SCHEMA, filters, FILTERS)}
          onClear={() => setFilters(defaultsOf(URL_SCHEMA))}
        />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('virtualAccountItems.readOnly')}
      </Typography>
      {failed !== null && (
        <ErrorState
          variant="section"
          error={failed.error}
          what={t('errors.what.virtualAccountItems')}
          onRetry={() => setTick((n) => n + 1)}
        />
      )}
      {failed === null && items === null && (
        <Stack spacing={1} aria-busy="true">
          {[0, 1, 2].map((id) => (
            <Skeleton key={id} height={64} />
          ))}
        </Stack>
      )}
      {items?.length === 0 && (
        <Typography>
          {t(status === 'open' ? 'virtualAccountItems.emptyOpen' : 'virtualAccountItems.empty')}
        </Typography>
      )}
      {items && (
        <Stack component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {items.map((item) => (
            <Box
              component="li"
              key={item.id}
              sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider' }}
            >
              <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <KindChip neutral label={t(`items.kind.${item.kind}`)} />
                <Typography
                  sx={{ flex: 1, minWidth: 120, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {item.body}
                </Typography>
                {item.status && <StatusChip domain="meetingItem" status={item.status} />}
              </Stack>
              <Link
                component={RouterLink}
                to={`/v/${virtualAccountId}/meetings/${item.meetingId}/${encodeURIComponent(item.occurrenceKey)}`}
                sx={{
                  display: 'inline-block',
                  mt: 1,
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                }}
              >
                {t(
                  names?.get(item.meetingId)
                    ? 'virtualAccountItems.meetingNamed'
                    : 'virtualAccountItems.meetingFallback',
                  {
                    meetingName: names?.get(item.meetingId),
                    when: formatInstant(item.occurrenceKey, locale, zone),
                  },
                )}
              </Link>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
